import { Box, SxProps, Theme } from '@mui/material';

import { useRichTextEditor } from './editor';
import { EditorToolbarButton } from './EditorToolbarButton';
import { useLinkDialog } from './LinkDialogContext';

type FloatingToolbarProps = Readonly<{
  isVisible: boolean;
  selectionRect: { top: number; left: number; width: number; height: number } | null;
}>;

export const FloatingToolbar = ({ isVisible, selectionRect }: FloatingToolbarProps) => {
  const { commands, activeStates } = useRichTextEditor();
  const { openLinkDialog } = useLinkDialog();

  if (!isVisible || !selectionRect) return null;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: Math.max(8, selectionRect.top - 48),
    left: selectionRect.left + selectionRect.width / 2,
    transform: 'translateX(-50%)',
    zIndex: 1300,
  };

  return (
    <Box style={style} sx={containerSx}>
      <EditorToolbarButton
        id="rt-float-bold"
        label="Bold"
        icon="Bold01"
        iconSize="16px"
        active={activeStates.bold}
        onClick={() => commands.toggleBold()}
      />
      <EditorToolbarButton
        id="rt-float-italic"
        label="Italic"
        icon="Italic01"
        iconSize="16px"
        active={activeStates.italic}
        onClick={() => commands.toggleItalic()}
      />
      <EditorToolbarButton
        id="rt-float-underline"
        label="Underline"
        icon="Underline01"
        iconSize="16px"
        active={activeStates.underline}
        onClick={() => commands.toggleUnderline()}
      />
      <EditorToolbarButton
        id="rt-float-align-left"
        label="Align left"
        icon="AlignLeft"
        iconSize="16px"
        active={activeStates.isAlignedLeft}
        onClick={() => commands.alignLeft()}
      />
      <EditorToolbarButton
        id="rt-float-align-center"
        label="Align center"
        icon="AlignCenter"
        iconSize="16px"
        active={activeStates.isAlignedCenter}
        onClick={() => commands.alignCenter()}
      />
      <EditorToolbarButton
        id="rt-float-link"
        label="Insert link"
        icon="Link01"
        iconSize="16px"
        active={activeStates.isLink}
        onClick={openLinkDialog}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  padding: backrs.spacing['spacing-xs'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-lg'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
});
